import { useLanguage, languages, Language } from "@/contexts/LanguageContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Globe } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

interface LanguageSelectorProps {
  compact?: boolean;
}

export const LanguageSelector = ({ compact = false }: LanguageSelectorProps) => {
  const { language, setLanguage } = useLanguage();
  const { t } = useTranslation();

  const handleChange = (code: string) => {
    const selected = languages.find(l => l.code === code);
    if (selected && selected.code !== language.code) {
      setLanguage(selected);
      toast({
        title: t('language.changed'),
        description: t('language.changedTo', { language: selected.native }),
      });
    }
  };

  if (compact) {
    return (
      <Select value={language.code} onValueChange={handleChange}>
        <SelectTrigger className="w-24 h-9">
          <SelectValue>
            <span className="flex items-center gap-2">
              <span>{language.flag}</span>
              <span>{language.code.toUpperCase()}</span>
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="bg-popover z-50">
          {languages.map((l) => (
            <SelectItem key={l.code} value={l.code}>
              <span className="flex items-center gap-2">
                <span>{l.flag}</span>
                <span>{l.code.toUpperCase()}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <Select value={language.code} onValueChange={handleChange}>
      <SelectTrigger className="w-56">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-muted-foreground" />
          <SelectValue>
            <span className="flex items-center gap-2">
              <span>{language.flag}</span>
              <span>{language.name}</span>
            </span>
          </SelectValue>
        </div>
      </SelectTrigger>
      <SelectContent className="bg-popover z-50">
        {languages.map((l) => (
          <SelectItem key={l.code} value={l.code}>
            <span className="flex items-center gap-3">
              <span className="text-lg">{l.flag}</span>
              <span className="font-medium">{l.name}</span>
              <span className="text-muted-foreground text-sm">{l.native}</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
